import { apmCaptureError } from '../apm'
import { type LOCALE } from '../i18n'
import { urlNormalizeUri } from '../url-handling'

import { cmsStoryblokSlugToPublicUrl } from './cmsStoryblokSlugToPublicUrl'
import { type StoryblokMultilink } from './storyblok'

const getUrlFromStoryblokMultilinkStory = (
  multilink: StoryblokMultilink,
): string | undefined => {
  const { anchor, story } = multilink

  const slug = story && 'full_slug' in story && story.full_slug

  if (!slug) {
    return undefined
  }

  let url = cmsStoryblokSlugToPublicUrl(slug, {
    addTenantHostFromPathPrefix: true,
  })

  if (anchor) {
    url += `#${anchor}`
  }

  return url
}

const getUrlFromStoryblokMultilinkUrl = (
  multilink: StoryblokMultilink,
  locale?: LOCALE,
): string | undefined => {
  const { url } = multilink

  if (!url) {
    return undefined
  }

  if (url.startsWith('#') || url.startsWith('?')) {
    return url
  }

  try {
    return new URL(url).toString()
  } catch {
    return urlNormalizeUri({
      locale,
      uri: url,
    })
  }
}

const getUrlFromStoryblokMultilinkEmail = (
  multilink: StoryblokMultilink,
): string | undefined => {
  const { email } = multilink

  if (!email) {
    return undefined
  }

  try {
    return new URL(email).toString()
  } catch {
    return `mailto:${email}`
  }
}

/**
 * Convert Storyblok Link field value into href compatible value based on user input or story information.
 *
 * `linktype` processing:
 * - `email` returns `mailto:` link
 * - `story` returns absolute URL based on story slug
 * - `url` returns link based on user input
 */
export const cmsGetStoryblokLinkHref = (
  multilink: StoryblokMultilink,

  /**
   * If provided, the relative link will be processed with the correct language prefix
   */
  locale?: LOCALE,
): string | undefined => {
  switch (multilink.linktype) {
    case 'email': {
      return getUrlFromStoryblokMultilinkEmail(multilink)
    }

    case 'story': {
      return getUrlFromStoryblokMultilinkStory(multilink)
    }

    case 'url': {
      return getUrlFromStoryblokMultilinkUrl(multilink, locale)
    }

    default: {
      apmCaptureError(
        new Error(
          `Link field type "${multilink.linktype}" is not supported yet.`,
        ),
      )

      return undefined
    }
  }
}
